/**
 * Home guides for bathrooms, floors, bedrooms, dusting, laundry, storage and personal hygiene (docs/HOME.md).
 * Every number and product comes from a research row cited in `citations` or `products`. Where the sources are
 * silent, the guide says so (`suggested`) instead of inventing a rule.
 */
import { guide, s, WHERE, WHERE_CITES, NEVER, NEVER_CITES, type HomeGuide } from "./guide-kit.ts";
import { clean, food, hygiene, laundry, storage, upholstery } from "./sources.ts";

const BATH_TOOLS_NOTE = { say: "Keep a separate bathroom mop and color-coded cloths (for example red for the toilet area). This is CDC healthcare-facility practice, applied at home; no home source was found.", buy: ["restroom_mop", "wiping_cloths"] } as const;

export const ROOM_GUIDES: HomeGuide[] = [
  /* ───────── bathroom ───────── */
  guide({
    id: "toilet", title: "Clean the toilet",
    when: ["Weekly (American Cleaning Institute).", "Clorox suggests a deeper clean of toilets every two weeks.", "After vomiting or diarrhea illness: disinfect right away (see Sick at home)."],
    need: ["A long-handled toilet brush.", "A toilet bowl cleaner with an EPA registration number on the label.", "Disinfecting wipes or spray for the seat, handle and outside.", { say: "Gloves.", buy: "cleaning_gloves" }, BATH_TOOLS_NOTE],
    steps: [
      "Put on gloves.",
      "Apply the bowl cleaner under and around the rim (Clorox Toilet Bowl Cleaner with Bleach: 6 oz).",
      "Scrub the whole bowl with the long-handled brush, reaching the rim holes and as far into the trap as you can.",
      "Let the cleaner sit for the time on its label (Clorox's label: 10 minutes), then flush.",
      "Wipe the seat, handle and outside of the toilet with disinfectant and keep it wet for the label's time (Clorox Disinfecting Wipes: visibly wet 4 minutes).",
      "Clean the toilet last, after the sink, mirror and tub (Clorox's order).",
      "Rinse the brush in the flushed bowl and let it drip dry over the bowl.",
      "Take off the gloves and wash your hands.",
    ],
    where: [WHERE.cleaners, WHERE.bathroomTools],
    never: [NEVER.mixBleach, "Never use another toilet bowl cleaner, rust remover or acid with a bleach toilet cleaner (Clorox label).", "Never use the toilet brush or toilet cloths anywhere else."],
    easy: { title: "Clean the toilet", why: "Toilets spread germs if they are not cleaned each week.", steps: [
      s("gloves", "Put on gloves."),
      s("chemical", "Squirt the bowl cleaner under the rim."),
      s("clean", "Scrub the whole bowl with the long brush."),
      s("wait", "Wait for the time on the label.", { waitSeconds: 600 }),
      s("water", "Flush the toilet."),
      s("spray", "Wipe the seat, handle and outside with disinfectant."),
      s("wait", "Leave it wet for the time on the label."),
      s("stop", "Never mix toilet cleaners with other cleaners."),
      s("hands", "Take off the gloves and wash your hands."),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1, 2, 4, 7, 8], fullIf: "After vomiting or diarrhea illness: disinfect right away (see Sick at home)." },
    citations: [...clean("B1", "B3", "B7", "B8", "B9", "B10", "K3", "G11", "F12"), ...WHERE_CITES.cleaners, ...NEVER_CITES.mixBleach],
    products: [
      { job: "Toilet bowl", examples: ["Clorox Toilet Bowl Cleaner with Bleach"], citations: clean("B7"), note: "Apply 6 oz under and around the rim; let it sit 10 minutes (label)." },
      { job: "Seat, handle and outside", examples: ["Clorox Disinfecting Wipes", "Lysol Disinfectant Spray"], citations: clean("K3", "K5"), note: "Keep wet for the label's time." },
    ],
    keywords: ["toilet", "bowl", "toilet brush", "bathroom", "restroom", "commode"],
  }),
  guide({
    id: "bathroom-sink", title: "Clean the bathroom sink and counter",
    when: ["Weekly (American Cleaning Institute).", "Rinse the sink after each use to prevent buildup (ACI)."],
    need: ["A disinfectant for the sink.", { say: "For buildup: a tub/tile/sink cleaner, a nonabrasive all-purpose cleaner, or a mild abrasive cleanser.", buy: ["bath_cleaner", "all_purpose_cleaner"] }, { say: "A cloth kept for the bathroom.", buy: "wiping_cloths" }],
    steps: [
      "Clear the counter.",
      "Wipe the sink, faucet and counter with disinfectant and keep them wet for the label's time (Clorox's guide for its bleach disinfectant: 6 minutes).",
      "For buildup, scrub with a tub/tile/sink cleaner or a mild abrasive cleanser.",
      "Rinse with clean water and wipe dry.",
      "Clean the faucet aerator screen now and then (see Faucet aerator).",
    ],
    where: [WHERE.cleaners, WHERE.bathroomTools],
    never: [NEVER.mixBleach, NEVER.foodContainer],
    easy: { title: "Clean the bathroom sink", why: "A clean sink keeps germs off your hands and brush.", steps: [
      s("box", "Clear the counter."),
      s("spray", "Spray the sink, faucet and counter with disinfectant."),
      s("wait", "Leave it wet for the time on the label."),
      s("clean", "Scrub any buildup with a sink cleaner."),
      s("water", "Rinse with clean water."),
      s("towel", "Wipe it dry."),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1, 2, 4, 5], fullIf: "For buildup, scrub with a tub/tile/sink cleaner or a mild abrasive cleanser." },
    citations: [...clean("B1", "B4", "B6", "B8"), ...WHERE_CITES.cleaners, ...NEVER_CITES.mixBleach, ...NEVER_CITES.foodContainer],
    keywords: ["sink", "bathroom sink", "vanity", "counter", "faucet"],
  }),
  guide({
    id: "shower-tub", title: "Clean the shower and tub",
    when: ["Weekly for high-moisture areas (Clorox).", "Rinse the tub after each use (American Cleaning Institute)."],
    need: [{ say: "A nonabrasive all-purpose cleaner, tub/tile/sink cleaner or liquid cleanser.", buy: ["bath_cleaner", "all_purpose_cleaner"] }, "A squeegee or towel.", { say: "Optional, Clorox's own method: 1/3 cup Clorox Disinfecting Bleach in 1 gallon of water, for the tub.", buy: "bleach" }],
    steps: [
      "Turn on the bathroom fan.",
      "Spray the tub and walls with a nonabrasive tub/tile cleaner and scrub.",
      "If you use Clorox's bleach method for the tub: 1/3 cup in 1 gallon of water, keep it wet 10 minutes, then rinse. This is Clorox's direction for its own product.",
      "Rinse everything well.",
      "Squeegee or towel the walls.",
      "Leave the door or curtain open so it dries.",
      "After each shower: rinse the tub and squeegee the walls.",
    ],
    where: [WHERE.cleaners, WHERE.bleachMix],
    never: [NEVER.mixBleach, "Never pair a bleach step with an acid or vinegar cleaner (Poison Control)."],
    easy: { title: "Clean the shower", why: "Damp tubs grow mildew. Cleaning and drying stops it.", steps: [
      s("power", "Turn on the fan."),
      s("spray", "Spray the tub and walls with tub cleaner."),
      s("clean", "Scrub the tub and walls."),
      s("water", "Rinse everything with clean water."),
      s("clean", "Wipe the walls dry with a squeegee or towel."),
      s("check", "Leave the door or curtain open to dry."),
      s("stop", "Never mix bleach with vinegar or other cleaners."),
    ] },
    citations: [...clean("B4", "B8", "B9", "G10"), ...WHERE_CITES.cleaners, ...WHERE_CITES.bleachMix, ...NEVER_CITES.mixBleach],
    products: [{ job: "Tub (Clorox's own method)", examples: ["Clorox Disinfecting Bleach"], citations: clean("B8"), note: "1/3 cup in 1 gallon of water; keep wet 10 minutes, then rinse." }],
    keywords: ["shower", "tub", "bathtub", "mildew", "shower walls"],
  }),
  guide({
    id: "mirrors", title: "Clean the bathroom mirror",
    when: ["Weekly (American Cleaning Institute)."],
    need: ["A clean cloth kept for the bathroom.", { say: "A cleaner whose label says it is for mirrors or glass.", buy: "glass_cleaner" }],
    steps: ["Spray a cleaner labeled for glass onto the cloth or mirror.", "Wipe from top to bottom.", "Buff dry with a clean, dry cloth."],
    where: [WHERE.cleaners, WHERE.cloths],
    never: ["Never use Clorox Disinfecting Wipes on mirrors: the wipes' label says not to. Clorox's bathroom guide says otherwise; the product label wins."],
    easy: { title: "Clean the mirror", why: "A clean mirror is part of a clean bathroom.", steps: [
      s("spray", "Spray glass cleaner on a clean cloth."),
      s("clean", "Wipe the mirror from top to bottom."),
      s("towel", "Wipe it dry with a dry cloth."),
      s("stop", "Never use disinfecting wipes on the mirror."),
    ] },
    citations: clean("B1", "K3"),
    keywords: ["mirror", "glass", "bathroom mirror"],
  }),
  guide({
    id: "bath-towels", title: "Wash the bath towels",
    when: ["At least weekly (Cleveland Clinic).", "Or after 3–5 normal uses (American Cleaning Institute), whichever comes first.", "Hang towels spread out on a bar between uses so they dry."],
    need: ["Laundry detergent (dose from its label).", { say: "Chlorine bleach for whites if the towels smell (label directions).", buy: "bleach" }],
    steps: ["Use the towel cycle or warm or hot water.", "Add bleach if they smell, following the bleach label.", "Dry on a long, high-heat setting until fully dry.", "Wash new towels before first use.", "Put them away only when completely dry."],
    where: [WHERE.towels, WHERE.laundry],
    never: [NEVER.shareRazor, "Never fold or store towels damp."],
    easy: { title: "Wash the bath towels", why: "Damp towels keep germs alive.", steps: [
      s("towel", "Put the towels in the washer."),
      s("soap", "Add detergent the way the label says."),
      s("water", "Use the towel cycle or warm water."),
      s("power", "Dry them on high heat until fully dry."),
      s("towel", "Hang towels spread out between uses."),
      s("stop", "Never share towels."),
    ] },
    citations: [...clean("B11"), ...laundry("L7", "L9", "M9", "M3", "M5"), ...WHERE_CITES.towels, ...NEVER_CITES.shareRazor],
    keywords: ["towels", "bath towels", "washcloths", "towel"],
  }),
  guide({
    id: "hand-towels", title: "Wash the hand towels",
    when: ["At least weekly (Cleveland Clinic).", "The older ACI list says every two days; either way, sooner if damp or dirty."],
    need: [{ say: "Laundry detergent.", buy: "laundry_detergent" }, { say: "Spare hand towels to swap in.", noBuy: "towels you already own" }],
    steps: ["Swap in a clean hand towel.", "Wash the used ones with the bath towels: towel cycle or warm or hot water.", "Dry on high heat until fully dry.", "Spread the towel on the bar so it dries between uses."],
    where: [WHERE.towels],
    never: ["Never leave a hand towel bunched and damp."],
    easy: { title: "Swap the hand towels", why: "Clean hands need a clean towel.", steps: [
      s("towel", "Hang a clean hand towel."),
      s("towel", "Put the used towel in the wash."),
      s("water", "Wash it warm with the bath towels."),
      s("power", "Dry it on high heat until fully dry."),
    ] },
    // 5-minute version: the first essential Easy steps, nothing new (guide-kit quick).
    quick: { steps: [0, 1] },
    citations: [...laundry("L8", "L9", "M9"), ...WHERE_CITES.towels, ...storage("H39")],
    keywords: ["hand towel", "hand towels", "towels"],
  }),
  guide({
    id: "bath-mats", title: "Wash the bath mats",
    when: ["Weekly (ACI list via USU Extension; Mayo Clinic says wash floor mats weekly)."],
    need: [{ say: "Laundry detergent.", buy: "laundry_detergent" }, { say: "The mat's care label.", noBuy: "the care label on your own item" }],
    steps: ["Shake the mat outdoors if you can.", "Wash it following its care label.", "Dry it completely before it goes back on the floor."],
    where: [WHERE.towels],
    never: ["Never put a damp mat back down: damp fabric grows mildew."],
    easy: { title: "Wash the bath mat", why: "Mats stay damp and hold germs.", steps: [
      s("box", "Pick up the bath mat."),
      s("water", "Wash it the way its label says."),
      s("power", "Dry it all the way."),
      s("check", "Put it back only when dry."),
    ] },
    citations: [...laundry("L8", "L15"), ...storage("H39"), ...WHERE_CITES.towels],
    keywords: ["bath mat", "rug", "floor mat"],
  }),
  guide({
    id: "bathroom-floor", title: "Mop the bathroom floor",
    when: ["Weekly: vacuum or mop floors in all rooms (American Cleaning Institute)."],
    need: [BATH_TOOLS_NOTE, "A floor cleaner that suits the floor type (see the tile or vinyl guide)."],
    steps: ["Clean the rest of the bathroom first.", "Sweep or vacuum up hair and dust.", "Mop with the bathroom's own mop and the right cleaner for the floor.", "Rinse and wring the mop before dipping it again; don't double-dip.", "Hang the mop head up to dry."],
    where: [WHERE.bathroomTools, WHERE.cleaners],
    never: ["Never use the bathroom mop in the kitchen.", NEVER.mixBleach],
    easy: { title: "Mop the bathroom floor", why: "Bathroom floors carry toilet germs.", steps: [
      s("clean", "Sweep up hair and dust."),
      s("water", "Fill the bucket with floor cleaner and water."),
      s("clean", "Mop with the bathroom mop only."),
      s("water", "Rinse and wring the mop before you dip again."),
      s("towel", "Hang the mop head up to dry."),
      s("stop", "Never use this mop in the kitchen."),
    ] },
    citations: [...clean("B2", "F12"), ...WHERE_CITES.bathroomTools, ...NEVER_CITES.mixBleach],
    keywords: ["bathroom floor", "mop", "bathroom mop"],
  }),
  guide({
    id: "grout", title: "Deep clean the grout",
    when: ["Every two weeks (Clorox's suggested frequency)."],
    need: [{ say: "For cement grout: an alkaline cleaner (TCNA names Spic and Span and Mr. Clean as examples).", buy: "all_purpose_cleaner" }, { say: "For mildew: ACI's grout-and-mildew mix, 3/4 cup of liquid household bleach in 1 gallon of water, or a mildew stain remover.", buy: "bleach" }, "A stiff brush and gloves."],
    steps: [
      "Pick one cleaner for the job: alkaline cleaner for dirt, or ACI's bleach mix for mildew. Never both at once.",
      "Put on gloves and open a window or run the fan.",
      "Apply the cleaner and scrub the grout lines.",
      "Wipe or wet-vac it up, rinse with clean water, then wet-vac or wipe again (TCNA's method).",
      "Seal grout later to prevent stains (TCNA).",
    ],
    where: [WHERE.cleaners, WHERE.bleachMix],
    never: ["Never use acids or vinegar on cement grout: they dissolve it (TCNA).", "Never pair a bleach grout step with an acid grout cleaner (Poison Control).", NEVER.mixBleach],
    easy: { title: "Clean the grout", why: "Grout holds dirt and mildew.", steps: [
      s("gloves", "Put on gloves."),
      s("power", "Turn on the fan or open a window."),
      s("chemical", "Pick one cleaner. Never mix two."),
      s("clean", "Scrub the grout lines with a stiff brush."),
      s("water", "Rinse with clean water."),
      s("towel", "Wipe up the water."),
      s("stop", "Never use vinegar or acid on grout."),
    ] },
    citations: [...clean("B5", "B9", "F11", "G10"), ...WHERE_CITES.cleaners, ...WHERE_CITES.bleachMix, ...NEVER_CITES.mixBleach],
    products: [
      { job: "Cement grout", examples: ["Spic and Span", "Mr. Clean"], citations: clean("F11"), note: "TCNA's examples of alkaline cleaners." },
      { job: "Grout mildew", examples: ["Liquid household bleach, 3/4 cup per gallon (ACI's grout mix only)"], citations: clean("B5") },
    ],
    keywords: ["grout", "tile grout", "mildew", "shower grout"],
  }),
  guide({
    id: "shower-curtain", title: "Wash the shower curtain and liner",
    when: ["Each season (American Cleaning Institute).", "Clorox suggests monthly for curtains, liners and rugs."],
    need: [{ say: "The curtain's and liner's care labels.", noBuy: "the care label on your own item" }, { say: "Laundry detergent.", buy: "laundry_detergent" }],
    steps: ["Take the curtain and liner down.", "Wash them as their care labels say.", "Hang them back up to dry fully, spread open."],
    where: [WHERE.towels],
    never: ["Never leave the curtain bunched: spread it so it dries."],
    easy: { title: "Wash the shower curtain", why: "Curtains stay damp and grow mildew.", steps: [
      s("box", "Take the curtain and liner down."),
      s("water", "Wash them the way the labels say."),
      s("towel", "Hang them back up spread open to dry."),
    ] },
    citations: [...clean("B2", "B9", "B4"), ...storage("H39"), ...WHERE_CITES.towels],
    keywords: ["shower curtain", "liner", "curtain"],
  }),
  guide({
    id: "faucet-aerator", title: "Clean the faucet aerator screens",
    suggested: "Cleveland Clinic says clean aerators \"regularly\" but gives no interval or method; follow your faucet maker.",
    when: ["Regularly (Cleveland Clinic gives no number)."],
    need: [{ say: "A soft cloth or old toothbrush.", buy: ["wiping_cloths", "scrub_brush"] }, { say: "Your faucet maker's instructions for taking off the aerator.", noBuy: "the maker's instructions, not a purchase" }],
    steps: ["Unscrew the aerator at the tip of the faucet the way the maker says.", "Rinse out sediment and debris from the screen.", "Turn it back on tight."],
    where: [WHERE.cleaners],
    never: ["Never force a part the maker says doesn't come off."],
    easy: { title: "Clean the faucet screen", why: "The screen catches grit and lead.", steps: [
      s("wrench", "Turn the tip of the faucet to take it off."),
      s("water", "Rinse the grit out of the screen."),
      s("wrench", "Turn it back on tight."),
    ] },
    citations: [...clean("B13"), ...WHERE_CITES.cleaners],
    keywords: ["aerator", "faucet screen", "faucet"],
  }),

  /* ───────── floors ───────── */
  guide({
    id: "vacuum-carpet", title: "Vacuum the carpet",
    when: ["By traffic (Carpet and Rug Institute): high-traffic or pet areas daily, medium about twice a week, light once a week.", "At least weekly everywhere (CRI; AAAAI/ACAAI guideline)."],
    need: [{ say: "A vacuum with a HEPA filter, if you can (EPA; American Lung Association).", buy: "vacuum" }, { say: "CRI suggests vacuums certified with its Seal of Approval.", buy: "vacuum" }, { say: "Bagged vacuums are better for air quality; bagless ones release dust when emptied (ALA).", buy: "vacuum" }],
    steps: [
      "Open windows if you can.",
      "Vacuum slowly, with about four overlapping passes, right to the edges (CRI).",
      "Vacuum under furniture and along walls, where moth and carpet beetle larvae feed (NMSU).",
      "Vacuum both sides of rugs (ALA).",
      "Empty the canister outdoors (ALA).",
      "Clean or replace filters as the vacuum's maker says.",
      "Treat spills right away (CRI).",
    ],
    where: ["Vacuum: in a closet, filters as its maker says (ALA)."],
    never: ["Never empty a bagless canister indoors (ALA).", "Someone with asthma or allergies should leave the room while you vacuum (EPA)."],
    easy: { title: "Vacuum the carpet", why: "Vacuuming picks up dust and allergens.", steps: [
      s("power", "Open a window if you can."),
      s("clean", "Run the vacuum slowly, four passes over each spot."),
      s("clean", "Go right to the edges and under furniture."),
      s("clean", "Run it over both sides of rugs."),
      s("trash", "Empty the canister outside."),
    ] },
    citations: [...clean("F1", "F2", "F3", "F4", "F5", "F6"), ...laundry("R1", "R4", "R6")],
    keywords: ["vacuum", "carpet", "rug", "hepa", "vacuuming"],
  }),
  guide({
    id: "carpet-deep-clean", title: "Deep clean the carpet",
    when: ["Every 12–18 months, by hot-water extraction (Carpet and Rug Institute).", "The American Lung Association says yearly."],
    need: [{ say: "A hot-water extraction cleaner or a professional; CRI suggests cleaners certified with its Seal of Approval.", noBuy: "you rent or hire this; no one product fits every carpet" }],
    steps: ["Vacuum first.", "Deep clean with hot-water extraction as the machine or cleaner says.", "Dry the carpet well: open windows and run fans (ALA)."],
    where: ["Carpet cleaners: original containers, away from food, out of reach of children and pets (Poison Control)."],
    never: ["Never leave carpet damp: dry it well (ALA)."],
    easy: { title: "Deep clean the carpet", why: "Deep cleaning lifts dirt vacuums miss.", steps: [
      s("clean", "Run the vacuum first."),
      s("water", "Use a hot-water carpet cleaner the way it says."),
      s("power", "Open windows and run fans to dry it."),
    ] },
    citations: [...clean("F4", "F6"), ...storage("H32")],
    keywords: ["carpet cleaning", "steam clean", "deep clean carpet", "extraction"],
  }),
  guide({
    id: "floor-hardwood", title: "Clean the hardwood floors",
    when: ["Vacuum or dust often; clean weekly with a spray mop and wood floor cleaner (Bona, for its lacquered floors).", "Deep clean occasionally, when needed (Bona)."],
    need: ["Bona Premium Spray Mop and Bona Wood Floor Cleaner (named by Bona).", "Bona Hardwood Floor Cleaner is for unwaxed, unoiled, polyurethane-finished wood."],
    steps: ["Vacuum or dust-mop the floor.", "Spray the wood floor cleaner and mop with the spray mop.", "Keep humidity at 40–60% (Bona).", "Check your floor's finish: waxed or oiled floors follow their own maker."],
    where: ["Floor cleaner: original container, away from food, out of reach of children (Poison Control)."],
    never: ["Never use a traditional mop and bucket on these floors: excess water and strong detergents damage them (Bona).", "Never use polishes or refreshers on this finish (Bona)."],
    easy: { title: "Clean the wood floor", why: "Water and strong soap hurt wood floors.", steps: [
      s("clean", "Run the vacuum or a dust mop."),
      s("spray", "Spray wood floor cleaner on a small area."),
      s("clean", "Mop it with the spray mop."),
      s("stop", "Never use a wet mop and bucket on wood."),
    ] },
    citations: [...clean("F7", "F8"), ...storage("H32")],
    products: [{ job: "Lacquered hardwood", examples: ["Bona Premium Spray Mop", "Bona Wood Floor Cleaner", "Bona OxyPower Wood Floor Deep Cleaner"], citations: clean("F7", "F8") }],
    keywords: ["hardwood", "wood floor", "bona", "floors"],
  }),
  guide({
    id: "floor-tile", title: "Mop the tile floors",
    when: ["Weekly: vacuum or mop floors in all rooms (American Cleaning Institute)."],
    need: [{ say: "Water: tile itself often needs only water (TCNA).", noBuy: "tap water" }, { say: "For cement grout: an alkaline cleaner (TCNA names Spic and Span and Mr. Clean).", buy: "all_purpose_cleaner" }, { say: "A mop and a second bucket of rinse water, so the mop is rinsed and wrung before re-dipping (CDC practice).", buy: "kitchen_mop" }],
    steps: ["Sweep or vacuum.", "Mop with water, or with an alkaline cleaner where grout is dirty.", "Rinse and wring the mop in the rinse bucket before dipping again.", "For heavy grout soil: apply cleaner, wet-vac it up, rinse, wet-vac again (TCNA).", "Hang the mop head up to dry."],
    where: [WHERE.cleaners, "Mop: hung head up to dry, away from food (CDC practice)."],
    never: ["Never use acids or vinegar on cement grout: they dissolve it (TCNA).", "Never double-dip a dirty mop (CDC practice)."],
    easy: { title: "Mop the tile floor", why: "Floors carry dirt from shoes and pets.", steps: [
      s("clean", "Sweep the floor."),
      s("water", "Fill one bucket with water or tile cleaner."),
      s("water", "Fill a second bucket with clean rinse water."),
      s("clean", "Mop a small area."),
      s("water", "Rinse and wring the mop before you dip again."),
      s("towel", "Hang the mop head up to dry."),
      s("stop", "Never use vinegar on grout."),
    ] },
    citations: [...clean("B2", "F11", "F12"), ...WHERE_CITES.cleaners],
    products: [{ job: "Cement grout", examples: ["Spic and Span", "Mr. Clean"], citations: clean("F11"), note: "TCNA's examples of alkaline cleaners." }],
    keywords: ["tile", "tile floor", "mop", "ceramic"],
  }),
  guide({
    id: "floor-vinyl", title: "Clean the vinyl floors",
    when: ["Weekly: vacuum or mop floors in all rooms (American Cleaning Institute)."],
    need: [{ say: "For luxury vinyl: Armstrong Flooring Once 'n Done no-rinse cleaner (named by Armstrong).", buy: "neutral_floor_cleaner" }, { say: "Your floor maker's instructions if it isn't Armstrong.", noBuy: "the maker's instructions, not a purchase" }],
    steps: ["Sweep or vacuum.", "Mop with the no-rinse cleaner as its label says.", "Let it dry."],
    where: ["Floor cleaner: original container, away from food, out of reach of children (Poison Control)."],
    never: ["Never use detergents, abrasive cleaners or \"mop and shine\" products (Armstrong).", "Never use paste wax or solvent-based polishes (Armstrong).", "Armstrong discourages heat and moisture, such as steam mops."],
    easy: { title: "Clean the vinyl floor", why: "The wrong cleaner dulls vinyl.", steps: [
      s("clean", "Sweep the floor."),
      s("spray", "Use the no-rinse floor cleaner the way it says."),
      s("clean", "Mop a small area at a time."),
      s("check", "Let it dry."),
      s("stop", "Never use a steam mop or wax."),
    ] },
    citations: [...clean("B2", "F9"), ...storage("H32")],
    products: [{ job: "Luxury vinyl", examples: ["Armstrong Flooring Once 'n Done"], citations: clean("F9") }],
    keywords: ["vinyl", "lvp", "luxury vinyl", "linoleum", "floor"],
  }),

  /* ───────── bedroom ───────── */
  guide({
    id: "sheets", title: "Wash the sheets and pillowcases",
    when: ["Weekly (Sleep Foundation, Cleveland Clinic, the AAAAI/ACAAI allergy guideline).", "At the latest every two weeks (American Cleaning Institute; Cleveland Clinic's outer limit).", "Every 3–4 days if pets sleep in the bed (Sleep Foundation).", "Pillowcases once a week (Sleep Foundation)."],
    need: ["Laundry detergent (gentle; a second rinse for sensitive skin).", { say: "The sheets' care labels.", noBuy: "the care label on your own item" }],
    steps: ["Strip the bed without shaking the linens.", "Wash in the hottest water the label allows (cotton usually hot, polyester warm).", "Weekly washing matters more than heat: the allergy guideline says high temperature is not needed.", "For dust-mite allergy, Mayo Clinic and ACAAI suggest 130°F or hotter; that's an option, not a requirement.", "Tumble dry low or hang, until completely dry."],
    where: [WHERE.towels, WHERE.laundry],
    never: ["Never shake dirty linens (nursing text).", "Keep home hot water below 120°F to avoid scalding (allergy guideline)."],
    easy: { title: "Wash the sheets", why: "Clean sheets each week cut dust mites and germs.", steps: [
      s("box", "Take the sheets and pillowcases off the bed."),
      s("stop", "Don't shake them."),
      s("water", "Wash them in the hottest water the label allows."),
      s("soap", "Use detergent the way the label says."),
      s("power", "Dry them low or hang them until fully dry."),
      s("check", "Make the bed with clean sheets."),
    ] },
    citations: laundry("L1", "L2", "L3", "L4", "L7", "L14", "L15", "L16", "M7", "B1", ...[]).concat(WHERE_CITES.towels),
    keywords: ["sheets", "bedding", "pillowcases", "wash sheets", "bed sheets", "linens"],
  }),
  guide({
    id: "make-bed", title: "Make the bed (hospital corners)",
    when: ["After the bed has aired: leave it unmade a while so sweat dries (Cleveland Clinic); pull the sheets back in the morning (Sealy)."],
    need: [{ say: "A fitted sheet, a flat sheet, a blanket or spread, pillowcases.", noBuy: "bedding you already own" }],
    steps: [
      "Stack clean linen in the order you'll use it; keep it off your clothes and off the floor (linen that touches the floor goes back to the wash).",
      "Put on the fitted sheet, seams toward the mattress, and smooth it.",
      "Lay the flat sheet over it.",
      "Mitered foot corner: tuck the whole end of the sheet under the foot of the mattress.",
      "Facing the foot of the bed, lift the side edge to make a 45-degree angle from the corner.",
      "Lay that angled piece on top of the bed and tuck in what hangs below.",
      "Drop the angled piece back over the side and tuck it under the mattress.",
      "Miter the blanket or spread the same way.",
      "Put on pillowcases; the open end faces away from the door.",
    ],
    where: [WHERE.towels],
    never: ["Never shake linens: it spreads germs in the air (nursing text).", "Never let clean linen touch the floor; if it does, wash it again."],
    easy: { title: "Make the bed", why: "Tight corners keep the bed neat all night.", steps: [
      s("check", "Let the bed air a while first."),
      s("towel", "Put on the fitted sheet and smooth it."),
      s("towel", "Lay the flat sheet on top."),
      s("clean", "Push the end of the sheet under the foot."),
      s("clean", "Lift the side edge into a slant at the corner."),
      s("clean", "Push the hanging part under the mattress."),
      s("clean", "Let the slant fall and push it under too."),
      s("towel", "Do the blanket the same way."),
      s("towel", "Put on the pillowcases, open end away from the door."),
    ] },
    suggested: "No source sets how often to make the bed; the method is from nursing texts.",
    citations: laundry("B1", "B2", "B3", "B6"),
    keywords: ["make the bed", "hospital corners", "mitered corners", "bed making", "tuck sheets"],
  }),
  guide({
    id: "duvet-cover", title: "Wash the duvet cover",
    when: ["Every 2 weeks to a month (Sleep Foundation)."],
    need: [{ say: "Laundry detergent.", buy: "laundry_detergent" }, { say: "The cover's care label.", noBuy: "the care label on your own item" }],
    steps: ["Take the cover off the duvet.", "Wash it following its care label, in the hottest water the label allows.", "Dry it completely before putting it back."],
    where: [WHERE.towels],
    never: ["Never put a damp cover back on."],
    easy: { title: "Wash the duvet cover", why: "The cover takes the dirt so the duvet stays clean.", steps: [
      s("box", "Take the cover off the duvet."),
      s("water", "Wash it the way the label says."),
      s("power", "Dry it all the way."),
      s("towel", "Put it back on the duvet."),
    ] },
    citations: [...laundry("L4", "M7", "M10"), ...WHERE_CITES.towels],
    keywords: ["duvet", "duvet cover", "bedding"],
  }),
  guide({
    id: "comforter", title: "Wash the comforter and blankets",
    when: ["Every 2–3 months (Sleep Foundation).", "Cleveland Clinic: every six months or as needed. Older ACI list: once or twice a year."],
    need: [{ say: "The care label.", noBuy: "the care label on your own item" }, { say: "A washer and dryer big enough, or a laundromat.", noBuy: "your own washer and dryer, or a laundromat" }],
    steps: ["Read the care label.", "Wash as it says.", "Dry it completely: a damp comforter grows mildew."],
    where: [WHERE.towels],
    never: ["Never store bedding damp."],
    easy: { title: "Wash the comforter", why: "Comforters collect dust and sweat over time.", steps: [
      s("check", "Read the care label."),
      s("water", "Wash it the way the label says."),
      s("power", "Dry it all the way."),
    ] },
    citations: [...laundry("L1", "L4", "L8", "M10"), ...WHERE_CITES.towels],
    keywords: ["comforter", "blanket", "blankets", "quilt"],
  }),
  guide({
    id: "pillows", title: "Wash the pillows",
    when: ["Every 4–6 months, if washable (Sleep Foundation).", "Cleveland Clinic: every six months or as needed."],
    need: [{ say: "The pillow's care label (some pillows aren't washable).", noBuy: "the care label on your own item" }],
    steps: ["Check the label says the pillow is washable.", "Wash it as the label says.", "Dry it completely before use."],
    where: [WHERE.towels],
    never: ["Never wash a pillow the label says not to."],
    easy: { title: "Wash the pillows", why: "Pillows hold sweat and dust mites.", steps: [
      s("check", "Check the label says you can wash it."),
      s("water", "Wash it the way the label says."),
      s("power", "Dry it all the way."),
    ] },
    citations: [...laundry("L1", "L4", "M10"), ...WHERE_CITES.towels],
    keywords: ["pillow", "pillows"],
  }),
  guide({
    id: "mattress-protector", title: "Wash the mattress protector",
    when: ["Every one to two months (Sleep Foundation).", "Right away after a spill, accident or illness."],
    need: [{ say: "Laundry detergent.", buy: "laundry_detergent" }, { say: "The protector's care label.", noBuy: "the care label on your own item" }],
    steps: ["Take the protector off.", "Wash it as its label says.", "Dry it on low or medium heat, not high: high heat shrinks it and damages the waterproof layer.", "Put it back only when fully dry."],
    where: [WHERE.towels],
    never: ["Never dry it on high heat (Sleep Foundation).", "Never put it back damp."],
    easy: { title: "Wash the mattress protector", why: "It keeps spills and sweat out of the mattress.", steps: [
      s("box", "Take the protector off the bed."),
      s("water", "Wash it the way the label says."),
      s("power", "Dry it low. Never on high heat."),
      s("check", "Put it back when fully dry."),
    ] },
    citations: [...laundry("L5", "L6", "L8", "MT5"), ...WHERE_CITES.towels],
    keywords: ["mattress protector", "mattress pad", "protector"],
  }),
  guide({
    id: "mattress-clean", title: "Vacuum and spot-clean the mattress",
    when: ["About every 6 months (Sleep Foundation)."],
    need: [{ say: "A vacuum with an upholstery tool.", buy: "vacuum" }, { say: "A gentle cleanser and a cloth dipped in cold water.", buy: ["mild_detergent", "wiping_cloths"] }, { say: "Optional: baking soda.", buy: "baking_soda" }],
    steps: ["Strip the bed.", "Vacuum the top and sides with the upholstery tool.", "Blot stains lightly with gentle cleanser and a cloth dipped in cold water; don't soak foam.", "Optional: sprinkle baking soda, leave it a few hours, then vacuum it up.", "Let it dry fully before making the bed."],
    where: ["Vacuum and cleanser: cleanser in its original container, out of reach of children (Poison Control)."],
    never: ["Never get a mattress wet (Tempur Sealy).", "Never use dry-cleaning fluid on a mattress (Tempur Sealy)."],
    easy: { title: "Clean the mattress", why: "Mattresses collect dust and sweat.", steps: [
      s("box", "Take everything off the bed."),
      s("clean", "Run the vacuum's upholstery tool over the top and sides."),
      s("water", "Press stains with a cloth dipped in cold water."),
      s("stop", "Never soak the mattress."),
      s("check", "Let it dry before you make the bed."),
    ] },
    citations: [...laundry("L4", "MT3", "MT4"), ...storage("H32")],
    keywords: ["mattress", "clean mattress", "vacuum mattress", "stains"],
  }),
  guide({
    id: "mattress-rotate", title: "Rotate the mattress",
    when: ["The mattress maker's instructions win.", "Foam, latex and newer innerspring: every 6–12 months; older innerspring: every 2–6 months (Sleep Foundation).", "Tempur-Pedic, Sealy and Stearns & Foster consumer mattresses: don't flip; rotating is optional (Sealy).", "Tempur Sealy's hotel guide: at least every 3 months, monthly preferred."],
    need: [{ say: "A second person to help lift.", noBuy: "a person, not a purchase" }],
    steps: ["Check the maker's label: one-sided or two-sided, and whether it's zoned (zoned mattresses may not be meant to rotate).", "One-sided: turn it 180° so the head end is at the foot.", "Two-sided: flip it over, if the maker says to."],
    where: ["Nothing to store."],
    never: ["Never flip a one-sided mattress (Sealy)."],
    easy: { title: "Turn the mattress", why: "Turning spreads the wear so it lasts.", steps: [
      s("check", "Check the label for one side or two."),
      s("people", "Get someone to help you lift."),
      s("clean", "Turn it so the head end is at the foot."),
      s("stop", "Don't flip a one-sided mattress."),
    ] },
    citations: laundry("MT1", "MT2", "H9"),
    keywords: ["rotate mattress", "flip mattress", "mattress", "turn mattress"],
  }),

  guide({
    id: "couch-clean", kind: "topic", title: "Clean a couch or upholstered chair",
    when: ["Vacuum often. Real leather: wipe weekly with a damp, soft cloth (Ashley Furniture).", "A spill: right away."],
    need: [{ say: "A vacuum with a crevice or upholstery tool.", buy: "vacuum" }, { say: "Clean, soft white cloths.", buy: "wiping_cloths" }, { say: "Mild dish soap and warm water, for cloth with code W and for vinyl.", buy: "dish_soap" }, { say: "The tag or the maker's care sheet.", noBuy: "the care label on your own item" }],
    steps: [
      "Check what it is made of: real leather, pleather or vinyl, or cloth.",
      "Read the tag or the care sheet first. It wins over this guide.",
      "Test any cleaner in a hidden spot first, such as under a cushion. Let it dry before you go on.",
      "Vacuum the seams and the crumbs first.",
      "Real leather: wipe with a damp, soft, lint-free cloth. Don't rub. Wipe the whole cushion, edge to edge, so no water ring forms.",
      "Pleather or vinyl: wipe with mild soap and warm water on a soft damp cloth. Then wipe with a cloth and plain water. Use a soft white cloth, not paper towels (Naugahyde).",
      "Cloth: find the code on the tag. W: water-based cleaner, a little mild dish soap in lukewarm water, dabbed, not soaked. S: dry-cleaning solvent only, no water. W/S: either, gentlest first. X: vacuum or brush only.",
      "A spill: blot it right away with a clean white cloth. Blot, don't rub.",
      "Vomit or diarrhea: wear gloves, scoop it up and bag it, then see the vomit guide. Don't put bleach on leather: ask the maker which disinfectant is safe.",
    ],
    where: [WHERE.cleaners],
    never: [
      "Never use bleach, disinfectant spray or household cleaners on leather (Clorox, Lysol, Ashley Furniture).",
      "Never use saddle soap or leather cleaners with oils, soaps or detergents (Ashley Furniture).",
      "Never use water on cloth with code S or X (Ashley Furniture).",
      "Never use paper towels or industrial solvents on vinyl (Naugahyde).",
      "Never use a solvent near a flame (Sailrite).",
    ],
    easy: { title: "Clean the couch", why: "A test in a hidden spot can save the couch.", steps: [
      s("check", "Find out if it is leather, vinyl or fabric."),
      s("sign", "Read the tag or the care sheet."),
      s("check", "Test in a hidden spot first."),
      s("clean", "Use the vacuum on the seams and crumbs."),
      s("water", "Wipe real leather gently with very little water."),
      s("soap", "Wash vinyl gently with warm, mild soapy water."),
      s("towel", "Blot a spill. Don't rub."),
      s("stop", "Never use bleach on leather."),
    ] },
    citations: upholstery("UP1", "UP2", "UP3", "UP4", "UP5", "UP9", "UP10", "UP11", "UP12", "UP13", "UP15", "UP17", "UP18", "UP19", "UP20", "UP22", "UP30", "UP31"),
    products: [{ job: "Leather care kit the maker names", examples: ["Millennium Leather Care Kit (Ashley Furniture)"], citations: upholstery("UP12", "UP13") }],
    keywords: ["couch", "sofa", "loveseat", "leather couch", "leather sofa", "leather", "pleather", "faux leather", "vinyl", "polyester", "microfiber", "fabric", "upholstery", "upholstered chair", "armchair", "recliner", "chair", "booth", "polyester chair", "fabric chair", "pleather chair", "pleather booth", "vinyl booth", "cleaning code", "clean couch"],
  }),

  /* ───────── dusting and surfaces ───────── */
  guide({
    id: "dusting", title: "Damp-dust the surfaces",
    suggested: "No authoritative source gives a dusting interval; the method (damp cloth) is sourced.",
    when: ["No official interval; weekly is a common starting point."],
    need: ["A damp cloth."],
    steps: ["Damp-wipe surfaces rather than dry-dusting, so dust is trapped, not stirred up (ACAAI).", "Include the tops of doors, windowsills and window frames (Mayo Clinic).", "Fewer dust collectors (knickknacks, books, magazines) means less to dust (Mayo Clinic)."],
    where: [WHERE.cloths],
    never: ["Never dry-dust or sweep dust into the air when someone has allergies (ACAAI)."],
    easy: { title: "Dust with a damp cloth", why: "A damp cloth traps dust instead of stirring it up.", steps: [
      s("water", "Wet a cloth and wring it out."),
      s("clean", "Wipe dust off shelves and tables."),
      s("clean", "Wipe door tops, windowsills and window frames."),
      s("water", "Rinse the cloth when it gets dirty."),
    ] },
    citations: [...laundry("R2", "R3"), ...WHERE_CITES.cloths],
    keywords: ["dust", "dusting", "shelves", "windowsills"],
  }),
  guide({
    id: "high-touch", title: "Wipe light switches, doorknobs and handles",
    when: ["Regularly (CDC gives no number).", "Disinfect when someone is sick or at higher risk (CDC)."],
    need: [{ say: "A cleaner for routine cleaning.", buy: "all_purpose_cleaner" }, "When disinfecting: an EPA-registered disinfectant, such as Clorox Disinfecting Wipes or Lysol Disinfectant Spray."],
    steps: ["Clean first: wipe off visible dirt.", "When someone is sick, disinfect: use enough that the surface stays wet for the label's time (Clorox Disinfecting Wipes: visibly wet 4 minutes).", "Let it dry.", "Wear gloves and wash hands after (EPA)."],
    where: [WHERE.cleaners],
    never: [NEVER.mixBleach, "Never use disinfecting wipes on unsealed wood, untreated stone or mirrors (Clorox label)."],
    easy: { title: "Wipe handles and switches", why: "Everyone touches these, so germs pass through them.", steps: [
      s("clean", "Wipe off dirt you can see."),
      s("spray", "Wipe with disinfectant until wet if someone is sick."),
      s("wait", "Leave it wet for the time on the label."),
      s("check", "Let it dry."),
      s("hands", "Wash your hands."),
    ] },
    citations: [...clean("G3", "G4", "G5", "G7", "K3", "K5"), ...WHERE_CITES.cleaners, ...NEVER_CITES.mixBleach],
    products: [{ job: "Disinfecting high-touch surfaces", examples: ["Clorox Disinfecting Wipes", "Lysol Disinfectant Spray"], citations: clean("K3", "K5") }],
    keywords: ["light switches", "doorknobs", "handles", "high-touch", "disinfect"],
  }),

  /* ───────── laundry and storage chores ───────── */
  guide({
    id: "washer-clean", title: "Clean the washing machine",
    when: ["Follow your washer's brand.", "Whirlpool: once a month (its manuals say monthly or every 30 cycles, whichever is sooner), or when the indicator lights or it smells.", "LG front loader: Tub Clean every 30 uses or when \"tcL\" shows."],
    need: [{ say: "Whirlpool: one affresh Washing Machine Cleaner tablet, or liquid chlorine bleach in the Clean Washer cycle.", buy: ["washer_cleaner", "bleach"] }, "LG: an oxygen-based washer cleaner at its own dose."],
    steps: ["Empty the washer.", "Whirlpool: put one affresh tablet in the basket (not the dispenser) and run Clean Washer; no such cycle? Use the highest water level and hottest water.", "LG: run Tub Clean with an oxygen-based cleaner.", "Don't open the door mid-cycle.", "Leave the door and drawer open afterward to dry.", "Clean the dispenser drawer as your manual says."],
    where: [WHERE.laundry],
    never: ["Never use thickened, easy-pour or no-splash bleach in a Whirlpool Clean Washer cycle (Whirlpool).", "Never use chlorine-based or acidic cleaners in an LG washer (LG).", NEVER.mixBleach],
    easy: { title: "Clean the washer", why: "Washers grow smells and mold inside.", steps: [
      s("box", "Take everything out of the washer."),
      s("chemical", "Put one washer cleaner tablet in the drum."),
      s("power", "Run the clean washer cycle."),
      s("stop", "Don't open the door until it ends."),
      s("check", "Leave the door open to dry."),
    ] },
    citations: [...clean("L1", "L2", "L3"), ...laundry("M12"), ...WHERE_CITES.laundry, ...NEVER_CITES.mixBleach],
    products: [
      { job: "Whirlpool washers", examples: ["affresh Washing Machine Cleaner tablets"], citations: clean("L1") },
    ],
    keywords: ["washer", "washing machine", "clean washer", "affresh", "smell"],
  }),
  guide({
    id: "closet-rotation", title: "Rotate closets for the season",
    suggested: "Organizers say sort by season and move off-season clothes out; no source gives an interval.",
    when: ["Each season is a starting point; no source gives an interval."],
    need: [{ say: "Airtight bags or containers for clean, fully dry off-season clothes.", buy: "storage_bags" }, { say: "Matching slim hangers; padded hangers for structured garments.", buy: "hangers" }],
    steps: ["Wash or dry-clean clothes before long-term storage (NPIC).", "Make sure they're completely dry; check the storage spot for dampness (NDSU).", "Pack clean, dry off-season clothes airtight (UC IPM).", "Clean the storage area first: floors, shelves, walls (NMSU).", "Move off-season clothes out of the main closet if you have room (NAPO).", "Sort what's left by type, season or color (NAPO)."],
    where: ["Off-season clothes: clean, dry and airtight, somewhere cool, dry and dust-free (UC IPM, NPIC)."],
    never: ["Never store soiled or damp clothes: moths feed on soiled fabric and damp boxes stay wet (NMSU, NDSU)."],
    easy: { title: "Swap closets for the season", why: "Clean, dry storage keeps moths and mildew away.", steps: [
      s("water", "Wash clothes before you store them."),
      s("power", "Dry them all the way."),
      s("clean", "Clean the storage shelves first."),
      s("box", "Put them in airtight bags or bins."),
      s("box", "Move them out of the main closet."),
      s("box", "Sort what is left by type or color."),
    ] },
    citations: [...storage("H40", "H43", "H44", "H46"), ...laundry("C1", "C3")],
    keywords: ["closet", "season", "off-season", "rotate clothes", "wardrobe"],
  }),

  /* ───────── topics: laundry by item ───────── */
  guide({
    id: "laundry-everyday", title: "Everyday laundry", kind: "topic",
    when: ["Leggings, T-shirts, underwear, socks and swimsuits after every wear; pajamas after 3–4 wears; bras 2–3 wears with a rest day (American Cleaning Institute)."],
    need: ["Laundry detergent, dosed with its own cap or scoop.", { say: "The care labels.", noBuy: "the care label on your own item" }],
    steps: ["Sort by color and care label; keep heavy soil apart from light soil (ACI).", "Wash lint-shedders (new towels, fleece) apart from lint-attractors (corduroy, permanent press) (ACI).", "Treat stains before drying: dryer heat can set them (ACI).", "Dose detergent by the label; slightly more in hard water (ACI, Tide).", "Don't overload: residue usually comes from overloading (Tide).", "A healthy household can wash in cold water (ACI).", "Care-label temperatures: hot 112–145°F, warm 87–111°F, cold up to 86°F (FTC)."],
    where: [WHERE.laundry],
    never: ["Never dry a stained item before treating the stain (ACI)."],
    easy: { title: "Do the laundry", why: "Sorting and the right dose get clothes clean.", steps: [
      s("box", "Sort clothes by color and care label."),
      s("box", "Keep very dirty clothes apart."),
      s("spray", "Spray stains before you wash."),
      s("soap", "Add detergent with its own cap."),
      s("stop", "Don't pack the washer too full."),
      s("water", "Use cold water for everyday loads."),
    ] },
    citations: [...laundry("L7", "M1", "M2", "M3", "M4", "M6", "M8"), ...WHERE_CITES.laundry],
    products: [{ job: "Detergent dose", examples: ["Tide"], citations: laundry("M3", "M4"), note: "Use the label and cap; more in hard water." }],
    keywords: ["laundry", "wash clothes", "detergent", "sort laundry", "stains", "cold water"],
  }),
  guide({
    id: "laundry-sheets", title: "Laundry: sheets and bedding", kind: "topic",
    when: ["Sheets weekly, at the latest every two weeks (see Wash the sheets)."],
    need: ["A gentle detergent; a second rinse for sensitive skin (Sleep Foundation)."],
    steps: ["Use the hottest water the label allows: cotton usually hot, polyester warm (Sleep Foundation).", "The allergy guideline says weekly washing matters and high temperature is not necessary.", "Allergy option: Mayo Clinic and ACAAI say 130°F or hotter for dust mites.", "Tumble dry low or hang, until completely dry."],
    where: [WHERE.towels],
    never: ["Keep home hot water below 120°F to avoid scalding (allergy guideline).", "Never shake dirty linens (nursing text)."],
    easy: { title: "Wash bedding", why: "Weekly washing controls dust mites.", steps: [
      s("check", "Read the care label for the hottest water."),
      s("water", "Wash on that setting with gentle detergent."),
      s("water", "Add a second rinse for sensitive skin."),
      s("power", "Dry low or hang until fully dry."),
    ] },
    citations: laundry("L2", "L14", "L15", "L16", "M7", "B1"),
    keywords: ["wash sheets", "bedding", "hot water", "dust mites", "allergy"],
  }),
  guide({
    id: "laundry-towels", title: "Laundry: towels", kind: "topic",
    when: ["Bath and hand towels at least weekly; washcloths a couple of times a week; two to three times a week if the house is humid; gym towels daily (Cleveland Clinic)."],
    need: ["Detergent; chlorine bleach for whites if they smell (label directions)."],
    steps: ["Use the towel cycle or warm or hot water.", "Use bleach if they smell.", "Dry on a long, high-heat setting.", "Wash new towels before first use.", "Hang towels spread out on a bar between uses."],
    where: [WHERE.towels],
    never: [NEVER.shareRazor],
    easy: { title: "Wash towels", why: "Damp towels keep germs alive.", steps: [
      s("water", "Use the towel cycle or warm water."),
      s("chemical", "Add bleach if they smell, the way its label says."),
      s("power", "Dry them long on high heat."),
      s("towel", "Hang towels spread out to dry between uses."),
    ] },
    citations: [...laundry("L9", "M9", "M5"), ...WHERE_CITES.towels, ...NEVER_CITES.shareRazor],
    keywords: ["towels", "washcloths", "wash towels", "smelly towels"],
  }),
  guide({
    id: "laundry-kitchen-towels", title: "Laundry: kitchen towels and dishcloths", kind: "topic",
    when: ["Change them often, commonly daily (University of Nebraska Extension).", "A towel used on raw meat, poultry or seafood juices goes straight into the laundry."],
    need: [{ say: "Detergent.", buy: "laundry_detergent" }, { say: "Paper towels for cleaning up raw-meat juice (FDA suggests considering them).", buy: "paper_towels" }],
    steps: ["Put used kitchen towels in the laundry, not back on the hook.", "Wash on the HOT cycle.", "Dry completely on a high setting."],
    where: [WHERE.cloths],
    never: ["Never reuse a towel that wiped raw meat juice."],
    easy: { title: "Wash kitchen towels", why: "Kitchen towels spread germs to food.", steps: [
      s("towel", "Put out a clean kitchen towel each day."),
      s("trash", "Put a towel used on raw meat in the wash now."),
      s("water", "Wash them on the hot cycle."),
      s("power", "Dry them all the way on high."),
    ] },
    citations: [...food("H33", "H34"), ...laundry("L10"), ...WHERE_CITES.cloths],
    keywords: ["kitchen towels", "dish towels", "dishcloths", "tea towels"],
  }),
  guide({
    id: "laundry-jeans", title: "Laundry: jeans", kind: "topic",
    when: ["About every 3 wears (American Cleaning Institute).", "At most every 10 wears, to keep the fit (Levi's). The two sources differ: pick what suits you."],
    need: [{ say: "Detergent.", buy: "laundry_detergent" }],
    steps: ["Turn jeans inside out.", "Wash in cold water (Levi's).", "Line-dry (Levi's)."],
    where: [WHERE.laundry],
    never: ["Never dry stained jeans before treating the stain (ACI)."],
    easy: { title: "Wash jeans", why: "Cold wash and line drying keep the fit.", steps: [
      s("box", "Turn the jeans inside out."),
      s("water", "Wash them in cold water."),
      s("towel", "Hang them to dry."),
    ] },
    citations: laundry("L7", "L13", "M2"),
    keywords: ["jeans", "denim", "how often wash jeans"],
  }),
  guide({
    id: "laundry-sick", title: "Laundry after vomiting or diarrhea", kind: "topic",
    when: ["Right away, when clothes or linens are soiled with vomit or diarrhea (CDC)."],
    need: ["Rubber or disposable gloves.", { say: "Detergent.", buy: "laundry_detergent" }, { say: "For bleach-safe whites: chlorine bleach FOR LAUNDRY, diluted as its label says. This is the laundry mix; it is never the counter mix or the vomit-cleanup mix.", buy: "bleach" }],
    steps: ["Put on gloves.", "Take the soiled items off right away and handle them without shaking.", "Wash with detergent and hot water on the longest cycle available (CDC).", "Chlorine bleach disinfects many whites and colorfast items, not wool or silk; add it about 5 minutes into the wash (ACI).", "Machine dry on the highest heat (CDC).", "Take off the gloves and wash your hands."],
    where: [WHERE.laundry],
    never: ["Never shake soiled laundry (CDC).", "Never use the laundry bleach dose for cleaning counters or vomit: each bleach mix has its own job.", "Color-safe (oxygen) bleach does not disinfect (ACI)."],
    easy: { title: "Wash sick laundry", why: "Stomach bug germs live on cloth. Hot water and heat kill them.", steps: [
      s("gloves", "Put on gloves."),
      s("stop", "Don't shake the dirty items."),
      s("box", "Put them straight in the washer."),
      s("water", "Wash hot on the longest cycle."),
      s("power", "Dry on the highest heat."),
      s("hands", "Take off the gloves and wash your hands.", { waitSeconds: 20 }),
    ] },
    citations: [...laundry("M5", "M8", "M11"), ...food("H46"), ...hygiene("C6"), ...WHERE_CITES.laundry],
    keywords: ["sick laundry", "vomit laundry", "norovirus laundry", "diarrhea", "stomach bug"],
  }),

  /* ───────── topics: hygiene ───────── */
  guide({
    id: "handwashing-home", title: "Wash your hands at home", kind: "topic",
    when: ["Before, during and after preparing food; before and after eating.", "Before and after caring for someone sick with vomiting or diarrhea, and treating a cut.", "After the toilet, diapers, blowing your nose, coughing or sneezing, touching animals or pet food, and garbage (CDC)."],
    need: [{ say: "Soap and clean running water, warm or cold (CDC).", buy: "hand_soap" }, { say: "A clean towel or air dryer.", buy: "paper_towels" }],
    steps: ["Wet your hands with clean running water, turn off the tap, apply soap.", "Lather the backs of your hands, between fingers and under nails.", "Scrub at least 20 seconds.", "Rinse under clean running water.", "Dry with a clean towel or air dryer."],
    where: ["Soap and a clean towel at every sink."],
    never: ["Never rely on hand sanitizer for norovirus: use soap and water (CDC)."],
    easy: { title: "Wash your hands", why: "Clean hands keep germs out of food and mouths.", steps: [
      s("water", "Wet your hands with running water."),
      s("soap", "Put soap on your hands."),
      s("hands", "Scrub backs, fingers and nails.", { waitSeconds: 20 }),
      s("water", "Rinse off all the soap."),
      s("towel", "Dry with a clean towel."),
    ] },
    citations: [...hygiene("C1", "C2", "C4"), ...food("H41", "H42", "H43")],
    keywords: ["handwashing", "wash hands", "hand washing", "soap", "20 seconds"],
  }),
  guide({
    id: "sick-at-home", title: "Someone is sick at home", kind: "topic",
    when: ["Vomiting or diarrhea (norovirus): while sick and for at least 2 days (48 hours) after symptoms stop (CDC)."],
    need: [{ say: "Soap and water.", buy: "hand_soap" }, { say: "For vomit or diarrhea cleanup: gloves, paper towels, a plastic bag, and bleach at 5–25 tablespoons of household bleach per gallon of water (CDC's vomit mix), or an EPA-registered norovirus disinfectant.", buy: ["gloves", "paper_towels", "trash_bags", "bleach", "norovirus_disinfectant"] }],
    steps: ["The sick person doesn't prepare or handle food, or care for others, until 48 hours after symptoms stop (CDC).", "Wash hands with soap and water; hand sanitizer doesn't work well against norovirus.", "People can still spread norovirus for 2 weeks or more after they feel better (CDC, snippet): keep washing hands well.", "Clean up vomit or diarrhea: gloves, wipe up with paper towels into a plastic bag, disinfect with the vomit mix and leave it at least 5 minutes, then clean again with soap and hot water.", "Disinfect the toilet, floor, counters, doorknobs, switches, phones and remotes (Cleveland Clinic).", "Wash soiled laundry right away (see Laundry after vomiting)."],
    where: [WHERE.cleaners, WHERE.bleachMix],
    never: ["Never prepare food for others while sick or within 48 hours after (CDC).", "Never count on disinfecting wipes or alcohol for norovirus unless the label claims it (Cleveland Clinic).", NEVER.mixBleach],
    easy: { title: "Someone is sick at home", why: "Stomach bugs spread fast through food and hands.", steps: [
      s("stop", "Stop cooking for others while you are sick."),
      s("wait", "Wait 48 hours after it stops before cooking again."),
      s("hands", "Wash hands with soap. Sanitizer is not enough."),
      s("gloves", "Put on gloves to clean up vomit."),
      s("trash", "Wipe it up with paper towels into a bag."),
      s("spray", "Spray the strong bleach mix on the spot.", { waitSeconds: 300 }),
      s("soap", "Wash the spot again with soap and hot water."),
      s("hands", "Wash your hands when done."),
    ] },
    citations: [...hygiene("C3", "C4", "C5", "C7"), ...food("H43", "H44", "H45"), ...clean("B12"), ...WHERE_CITES.cleaners, ...WHERE_CITES.bleachMix, ...NEVER_CITES.mixBleach],
    keywords: ["sick", "norovirus", "stomach bug", "vomit", "throw up", "48 hours", "diarrhea"],
  }),
  guide({
    id: "personal-items", title: "Personal items: razors, towels, nails", kind: "topic",
    when: ["Every day, for every person in the home."],
    need: [{ say: "Your own towel, washcloth and razor.", noBuy: "personal items each person picks for themselves" }, { say: "Shaving cream or gel.", noBuy: "a personal item each person picks" }, { say: "Nail clippers.", noBuy: "a personal item each person picks" }],
    steps: ["Don't share towels, washcloths or razors (CDC, AAD).", "Keep cuts covered until healed (CDC).", "Shaving: wet the skin, use shaving cream or gel, shave with the grain, rinse after each swipe (AAD).", "Change the blade or throw away a disposable razor after 5–7 shaves (AAD).", "Let the razor dry completely between shaves; don't keep it in the shower or on a wet sink (AAD).", "Keep nails short, scrub under them with soap and water or a nail brush, clean clippers and files before use, don't bite nails (CDC)."],
    where: ["Razors: stored dry, out of the shower (AAD).", WHERE.towels],
    never: [NEVER.shareRazor],
    easy: { title: "Keep personal items your own", why: "Shared razors and towels spread skin germs.", steps: [
      s("stop", "Never share a razor, towel or washcloth."),
      s("bandage", "Keep cuts covered until they heal."),
      s("water", "Wet your skin and use shaving cream."),
      s("wrench", "Replace the blade after 5 to 7 shaves."),
      s("check", "Let the razor dry outside the shower."),
      s("clean", "Keep nails short and scrub under them."),
    ] },
    citations: [...hygiene("C8", "A1", "A3", "A4", "C9"), ...NEVER_CITES.shareRazor],
    keywords: ["razor", "shaving", "share towels", "nails", "personal hygiene", "hygiene"],
  }),
  guide({
    id: "beard-trimmings", title: "Beard trimmings and clipper care", kind: "topic",
    suggested: "No health or cleaning authority covers beard trimmings; this is the general sink method.",
    when: ["After each trim or shave."],
    need: [{ say: "A sink cleaner or disinfectant.", buy: "disinfectant" }, { say: "A small cleaning brush for the clipper.", buy: "clipper_brush" }, { say: "Wahl Clipper Oil for Wahl clippers.", buy: "clipper_oil" }],
    steps: ["Rinse the sink after each use (ACI).", "Wipe it with a sink cleaner or disinfectant (ACI).", "Clipper: after each use, take off the blade and brush out loose hair (Wahl).", "Oil the blades: three drops on the front teeth and one on each side (Wahl).", "Follow your clipper's maker on water: Wahl USA says never use water on the blades; Wahl Professional uses a disinfectant spray left no more than 2 minutes, then wiped off.", "Store blades in a clean, dry place (Wahl).", "Wash your hands after touching your beard or face, before food."],
    where: ["Clipper and blades: clean, dry place, away from the kitchen and food (Wahl; hygiene guidance)."],
    never: ["Never trim or shave in the kitchen or near food.", "Never use water on Wahl consumer clipper blades (Wahl USA)."],
    easy: { title: "Clean up after trimming", why: "Loose hair and skin belong down the drain, not on food.", steps: [
      s("water", "Rinse the sink after you trim."),
      s("spray", "Wipe the sink with sink cleaner."),
      s("wrench", "Take the blade off the clipper."),
      s("clean", "Clean the loose hair out with the brush."),
      s("wrench", "Oil the blade the way the maker says."),
      s("stop", "Don't put water on the blades unless the maker allows."),
      s("box", "Store the clipper in a clean, dry place."),
    ] },
    citations: [...clean("B4", "B6", "B14"), ...hygiene("W1", "W2", "W3", "W4", "W5", "A5")],
    products: [
      { job: "Clipper blades", examples: ["Wahl Clipper Oil", "Wahl Clini-Clip (professional)"], citations: hygiene("W2", "W3") },
    ],
    keywords: ["beard", "beard trimmings", "clippers", "trimmer", "shaving", "whiskers", "hair in sink"],
  }),
  guide({
    id: "bathroom-readiness", title: "Get the bathroom guest-ready", kind: "topic",
    when: ["Before guests arrive, and as part of the weekly bathroom clean."],
    need: ["Hand soap.", { say: "A fresh hand towel.", noBuy: "a towel you already own" }, "Toilet paper.", { say: "Disinfectant for the sink and toilet.", buy: "disinfectant" }],
    steps: ["Stock hand soap and a clean hand towel so everyone can wash their hands (CDC).", "Put out fresh hand and bath towels.", "Clean the toilet and sink if they're due (weekly, ACI).", "Wipe the mirror.", "Empty the bathroom trash.", "Give each person their own towel (CDC, AAD)."],
    where: [WHERE.cleaners, WHERE.towels],
    never: [NEVER.shareRazor],
    easy: { title: "Get the bathroom ready", why: "A stocked, clean bathroom lets everyone wash up.", steps: [
      s("soap", "Check the hand soap.", { check: { ask: "Is there hand soap?", ifNo: "Restock it now." } }),
      s("towel", "Hang a fresh hand towel."),
      s("restroom", "Check the toilet paper.", { check: { ask: "Is there toilet paper?", ifNo: "Restock it now." } }),
      s("clean", "Clean the toilet and sink if they are due."),
      s("clean", "Wipe the mirror."),
      s("trash", "Empty the bathroom trash."),
    ] },
    citations: [...hygiene("C2", "C8", "A1"), ...clean("B1"), ...WHERE_CITES.cleaners, ...WHERE_CITES.towels],
    keywords: ["guest", "guests", "guest-ready", "bathroom ready", "company coming"],
  }),
  guide({
    id: "closet-storage", title: "Store clothes in closets and drawers", kind: "topic",
    when: ["Whenever you put clothes away or pack them for the season."],
    need: [{ say: "Matching slim hangers; padded hangers for structured garments.", buy: "hangers" }, { say: "Airtight containers for clean, dry woolens.", buy: "storage_bags" }, { say: "Drawer organizers.", noBuy: "optional; any drawer organizer you like" }],
    steps: ["Fold sweaters on a shelf or in a drawer; hanging stretches them (OSU Extension).", "Hang sturdy structured or tailored garments on padded hangers (National Park Service).", "Store only clean, fully dry clothes; dry-clean woolens first (NMSU, UF IFAS).", "Store clean woolens airtight; moths feed on wool, silk, feathers, leather and soiled fabric (UC IPM, NMSU).", "Cedar helps only in a tightly sealed closet or chest, and its oils fade after several years (NMSU, UF).", "Mothballs work only in a tight closet, trunk or chest, kept away from children and pets; follow the label (UF IFAS).", "Keep indoor humidity below 60%, ideally 30–50% (EPA); hang clothes loosely so air moves (MU Extension).", "File-fold T-shirts to stand upright in drawers (KonMari)."],
    where: ["Mothballs and moth repellents: above the items, never touching fabric, out of reach of children and pets (NMSU, UF IFAS)."],
    never: ["Never store damp or dirty clothes (NDSU, NMSU).", "Never use mothballs in open living spaces: they only work airtight and are toxic (UC IPM)."],
    easy: { title: "Put clothes away right", why: "Clean, dry clothes stored well stay free of moths and mildew.", steps: [
      s("box", "Fold sweaters on a shelf. Don't hang them."),
      s("towel", "Hang coats and suits on padded hangers."),
      s("check", "Store only clean, dry clothes."),
      s("box", "Put clean wool in airtight bins."),
      s("towel", "Hang clothes loosely so air moves."),
      s("stop", "Keep mothballs away from children and pets."),
    ] },
    citations: [...storage("H38", "H39", "H40", "H41", "H42", "H43", "H44", "H45"), ...laundry("C1", "C2", "C3", "C4", "C5", "C6", "C7")],
    keywords: ["closet", "sweaters", "hangers", "moths", "mothballs", "cedar", "store clothes", "drawers"],
  }),
];
